/**
 * チャットへ添付されたファイルの振り分けと、PDF・テキストを document ブロックへ変換するユーティリティ。
 */
import type { DocumentContentBlock } from "@/lib/chat";
import { ACCEPTED_IMAGE_MEDIA_TYPES } from "@/lib/image-attachments";

// バックエンドの ChatMessageContentValidator と制限値を同期すること。
export const MAX_DOCUMENTS_PER_MESSAGE = 4;
export const MAX_PDF_BYTES = 10 * 1024 * 1024;
export const MAX_TEXT_BYTES = 1024 * 1024;
// 画像・文書を合わせた 1 メッセージの添付合計。base64 化しても Anthropic のリクエスト上限 (32MB) に収まる値。
export const MAX_ATTACHMENT_BYTES_PER_MESSAGE = 20 * 1024 * 1024;

const PDF_MEDIA_TYPE = "application/pdf";

// ブラウザが MIME を付けない (または .ts を video/mp2t とする) 形式があるため、拡張子を優先して判定する。
const TEXT_EXTENSIONS: ReadonlySet<string> = new Set([
	"txt",
	"md",
	"markdown",
	"csv",
	"tsv",
	"json",
	"jsonl",
	"yaml",
	"yml",
	"toml",
	"xml",
	"html",
	"css",
	"log",
	"py",
	"js",
	"jsx",
	"ts",
	"tsx",
	"cs",
	"go",
	"rs",
	"java",
	"c",
	"h",
	"cpp",
	"sh",
	"sql",
]);

/** 添付の振り分け先。image / document は本文に入れ、workspace はワークスペースへアップロードする。 */
export type AttachmentRoute = "image" | "document" | "workspace";

export interface ComposerDocument {
	id: string;
	kind: "pdf" | "text";
	name: string;
	/** PDF は base64 (`data:` プレフィックスなし)、テキストは本文そのもの。 */
	data: string;
	byteSize: number;
}

function extensionOf(name: string): string {
	const dot = name.lastIndexOf(".");
	return dot <= 0 ? "" : name.slice(dot + 1).toLowerCase();
}

function isPdf(file: File): boolean {
	return file.type === PDF_MEDIA_TYPE || extensionOf(file.name) === "pdf";
}

function isText(file: File): boolean {
	return TEXT_EXTENSIONS.has(extensionOf(file.name)) || file.type.startsWith("text/");
}

export function classifyAttachment(file: File): AttachmentRoute {
	// 空のファイルは document として送れない (バックエンドが空データを拒否する) ためワークスペースへ回す。
	if (file.size === 0) return "workspace";
	if ((ACCEPTED_IMAGE_MEDIA_TYPES as readonly string[]).includes(file.type)) return "image";
	if (isPdf(file)) return file.size <= MAX_PDF_BYTES ? "document" : "workspace";
	if (isText(file)) return file.size <= MAX_TEXT_BYTES ? "document" : "workspace";
	return "workspace";
}

function bytesToBase64(bytes: Uint8Array): string {
	// String.fromCharCode の引数上限を超えないよう分割して変換する。
	const chunkSize = 0x8000;
	let binary = "";
	for (let i = 0; i < bytes.length; i += chunkSize) {
		binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
	}
	return btoa(binary);
}

/**
 * document として添付できる形へ変換する。UTF-8 として読めないテキストは本文に入れられないため
 * null を返し、呼び出し側でワークスペースへ回す。
 */
export async function processDocumentFile(file: File): Promise<ComposerDocument | null> {
	const bytes = new Uint8Array(await file.arrayBuffer());
	const base = { id: crypto.randomUUID(), name: file.name, byteSize: file.size };
	if (isPdf(file)) return { ...base, kind: "pdf", data: bytesToBase64(bytes) };
	try {
		return { ...base, kind: "text", data: new TextDecoder("utf-8", { fatal: true }).decode(bytes) };
	} catch (err) {
		if (err instanceof TypeError) return null;
		throw err;
	}
}

export function toDocumentBlock(document: ComposerDocument): DocumentContentBlock {
	return document.kind === "pdf"
		? {
				type: "document",
				source: { type: "base64", media_type: PDF_MEDIA_TYPE, data: document.data },
				title: document.name,
			}
		: {
				type: "document",
				source: { type: "text", media_type: "text/plain", data: document.data },
				title: document.name,
			};
}
